import { TrendingUp } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import ChipLabel from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import { type UserNodeReportsDashboardResponse, useGrowersDashboardReports } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import type { Grower } from '/@/types/growers';
import { objectEntries } from '/@/utils';
import { getCrops } from '/@/views/dashboard/hooks/crops';

type CropType = keyof UserNodeReportsDashboardResponse['harvest']['avgYieldByCrop'];
const YieldByCropCell = ({ grower, cropFilter, year }: { grower: Grower; cropFilter?: CropType; year: number }) => {
    const units = useUnitProvider();
    const crops = getCrops();

    const cropMonitoringResultStatistics = grower.cropMonitoringResultStatistics;
    const avgYieldByCrop = cropMonitoringResultStatistics.harvest.avgYieldByCrop;
    const growersDashboardReportsQuery = useGrowersDashboardReports({
        year,
    });
    const allGrowersCropMonitoringResultStatistics = growersDashboardReportsQuery.data;

    const renderCropYield = (
        crop: CropType,
        cropData: (typeof crops)[keyof typeof crops],
        yieldValue: number,
        expanded: boolean
    ) => {
        if (!allGrowersCropMonitoringResultStatistics) return null;

        const allGrowersMeanYield =
            allGrowersCropMonitoringResultStatistics.harvest?.avgYieldByCrop?.[crop] /
            allGrowersCropMonitoringResultStatistics.harvest?.growersByCrop?.[crop];

        const differencePercentage = ((yieldValue - allGrowersMeanYield) / allGrowersMeanYield) * 100;

        let subtitle = null;
        if (differencePercentage > 0) {
            subtitle = (
                <Stack direction='row' spacing={0.5} alignItems='center'>
                    <TrendingUp
                        sx={{
                            color: 'success.main',
                        }}
                    />
                    <Typography variant='body2' color='success.main'>
                        {_('{{percentage}}% por encima del promedio de tus productores', {
                            percentage: differencePercentage.toFixed(2),
                        })}
                    </Typography>
                </Stack>
            );
        } else if (differencePercentage < 0) {
            subtitle = (
                <Stack direction='row' spacing={0.5} alignItems='center'>
                    <TrendingUp />
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {_('{{percentage}}% por debajo del promedio de tus productores', {
                            percentage: differencePercentage.toFixed(2),
                        })}
                    </Typography>
                </Stack>
            );
        }

        return (
            <TableTemplateCell
                titleSize='body2'
                title={
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                        <ChipLabel
                            label={cropData?.name + ':'}
                            type={crop}
                            text={units.toYield(yieldValue, crop, 2).toString()}
                            icon={cropData?.icon}
                        />
                    </Stack>
                }
                subtitle={expanded && subtitle}
            />
        );
    };

    if (cropFilter && cropFilter !== 'fallow') {
        return (
            <Stack spacing={0.5}>
                {renderCropYield(cropFilter, crops[cropFilter], avgYieldByCrop[cropFilter], true)}
            </Stack>
        );
    }

    return (
        <Stack spacing={0.5} direction='row'>
            {objectEntries(crops)
                .filter(([crop]) => avgYieldByCrop[crop as CropType] !== undefined)
                .map(([crop, cropData]) => {
                    const yieldValue = avgYieldByCrop[crop as CropType];
                    return renderCropYield(crop as CropType, cropData, yieldValue, false);
                })}
        </Stack>
    );
};

export default YieldByCropCell;
